import { useState } from 'react';
import { useAtomValue } from 'jotai';
import Dropzone from 'react-dropzone';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ClearIcon from '@mui/icons-material/Clear';
import lzSCommonsLastErrorMessageAtom from '@lz-atoms/Commons/lastErrorMessage';
import { lzFBytesToSize } from '@lz/services/Utils';
import { lzFGetTimestamp } from '@lz/services/Moment';
import useLzFApiCall from '@lz/services/Api';
import LzMBase from '@lz/models/commons/Base';
import LzMMedia from '@lz/models/commons/Media';

export interface LzPMediaUploader {
  isPrivate?: boolean;
  category?: string;
  accept?: string | Array<string>;
  minSize?: number;
  maxSize?: number;
  maxFiles?: number;
  onUploaded: (medias: Array<LzMBase>, filesErrors: Array<string>) => void;
}

const LzXMediaUploader = (props: LzPMediaUploader) => {
  const [files, setFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [fileUploadingIndex, setFileUploadingIndex] = useState(-1);
  const [fileUploadingProgress, setFileUploadingProgress] = useState(-1);
  const [fileUploadingFailures, setFileUploadingFailures] = useState<number[]>(
    []
  );
  const [filesErrors, setFilesErrors] = useState<string[]>([]);

  const lastErrorMessageState = useAtomValue(lzSCommonsLastErrorMessageAtom);

  const lzApiCall = useLzFApiCall();

  // Just set this for testing of upload simulation instead of actual upload (MUST BE FALSE IN PRODUCTION)
  const simulateUpload = false;

  const onFilesSelected = (files: File[]) => {
    const newFiles = files.slice();
    newFiles.push(...files);
    setFiles(newFiles);
  };

  const onFileRemove = (dex: number) => {
    const newFiles = files.slice();
    newFiles.splice(dex, 1);
    setFiles(newFiles);
  };

  const uploadFile = async (file: File) => {
    setFileUploadingProgress(0);

    if (simulateUpload) {
      for (let i = 0; i < 100; i++) {
        let c = 0;
        for (let j = 0; j < 99999; j++) {
          c = lzFGetTimestamp();
        }
        console.log('PROGRESS:', i, c);
        setFileUploadingProgress(i);
        if (Math.random() < 0.1) {
          return 'Random error uploading file!';
        }
      }
      return {}; // success
    }

    // Make the api call to upload the file
    const data = await lzApiCall(
      'media',
      {
        file: file,
        category: props.category || 'general',
        is_private: !!props.isPrivate,
      },
      'post',
      false,
      true
    );

    // TODO: need to figure out how we can do this
    // setFileUploadingProgress(p && p.total && p.total > 0 ? Math.round(p.loaded / p.total * 100) : 0);

    // Load data if call was successful
    if (data) {
      return data;
    }

    return false;
  };

  const onUploadFiles = async () => {
    // Array of medias for return
    const medias = [];

    // Failed files
    const failedFiles = [];
    const filesErrors = [];

    // Load up the progress
    setIsUploading(true);
    setFileUploadingIndex(0);
    setFileUploadingProgress(-1);
    setFileUploadingFailures([]);
    setFilesErrors([]);

    // Go through all files to process
    let i = 0;
    for (const file of files) {
      i++;

      // Try to upload file and get media in return
      const media = await uploadFile(file);
      console.log('UPLOAD-RESULT', media, lastErrorMessageState);
      const failuresDexes = [];

      // If failed then error message is returned as string
      if (typeof media === 'boolean') {
        failedFiles.push(file); // Save the failed file object for retry
        filesErrors.push(lastErrorMessageState); // Save the error for that file
        failuresDexes.push(i - 1);
      }

      // If it was not an error then push to medias
      else {
        medias.push(new LzMMedia(media));
      }

      // Update the progress-bar and error response during uploading
      setFileUploadingIndex(i);
      setFileUploadingFailures(failuresDexes);
    }

    // Finish up the progress bar and set results
    setFiles(failedFiles);
    setIsUploading(false);
    setFileUploadingIndex(-1);
    setFileUploadingProgress(-1);
    setFileUploadingFailures([]);
    setFilesErrors(filesErrors);

    if (props.onUploaded) {
      props.onUploaded(medias, filesErrors);
    }
  };

  const onClearAllFiles = () => {
    setFiles([]);
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        margin: '1em',
        height: '100%',
      }}
    >
      {files && files.length > 0 && (
        <Paper variant='outlined'
          style={{
            padding: '0.6em',
            margin: '0 -1em 0.6em -1em',
          }}
        >
          <Grid container spacing={1}>
            {files.map((file, dex) => (
              <Grid key={'file-' + dex} style={{ flexGrow: 1, maxWidth: 240 }}>
                <Card variant='outlined' className='lz-media-card'>
                  <CardHeader
                    action={
                      <IconButton
                        disabled={isUploading}
                        onClick={() => onFileRemove(dex)}
                      >
                        <ClearIcon />
                      </IconButton>
                    }
                    title={file.name}
                    subheader={lzFBytesToSize(file.size)}
                  />
                  <CardMedia
                    image={
                      LzMMedia.isImageByContentType(file.type)
                        ? LzMMedia.getImageIconByFile(file)
                        : null
                    }
                    children={
                      !LzMMedia.isImageByContentType(file.type)
                        ? LzMMedia.getImageIconByFile(file)
                        : null
                    }
                    title={file.name}
                  />
                  <CardContent>
                    {filesErrors[dex] && (
                      <Alert severity='error'>{filesErrors[dex]}</Alert>
                    )}
                    {isUploading && (
                      <LinearProgress
                        variant='determinate'
                        color={
                          fileUploadingFailures.indexOf(dex) < 0
                            ? 'primary'
                            : 'secondary'
                        }
                        value={
                          dex === fileUploadingIndex
                            ? fileUploadingProgress
                            : fileUploadingIndex < dex
                              ? 0
                              : 100
                        }
                      />
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          <Grid
            container
            justifyContent='flex-end'
            spacing={1}
            style={{ paddingTop: 12 }}
          >
            <Grid style={{ flexGrow: 1, alignSelf: 'center' }}>
              {isUploading && (
                <LinearProgress
                  variant='determinate'
                  value={Math.round((fileUploadingIndex / files.length) * 100)}
                />
              )}
            </Grid>
            <Grid>
              <Button disabled={isUploading} onClick={() => onClearAllFiles()}>
                Clear All
              </Button>
            </Grid>
            <Grid>
              <Button
                disabled={isUploading}
                color='primary'
                variant='contained'
                onClick={() => onUploadFiles()}
              >
                Upload
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      <Dropzone
        accept={LzMMedia.getDropzoneAcceptParam(props.accept)}
        minSize={props.minSize}
        maxSize={props.maxSize}
        maxFiles={props.maxFiles}
        onDrop={(acceptedFiles: File[]) => onFilesSelected(acceptedFiles)}
      >
        {({ getRootProps, getInputProps }) => (
          <section
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              margin: '0 -1em',
              height: '100%',
              border: '2px dashed #c0c0c0',
            }}
          >
            <div
              {...getRootProps()}
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                height: '100%',
              }}
            >
              <input {...getInputProps()} />
              <Typography
                variant='overline'
                display='block'
                style={{ textAlign: 'center' }}
              >
                Drag 'n' drop some files here, or click to select files
              </Typography>
            </div>
          </section>
        )}
      </Dropzone>
    </div>
  );
};

export default LzXMediaUploader;
